import { BackEndValueType, ESdChangeReason, IActionParameter } from "@kuika/kuika-cl-model";
import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../shared/pure-sd";
import { getDataTypeIconName } from "../../../shared/utility/data-type-icon";
import NoPropertiesActionSvg from "../../../svgs/no-properties-action.svg";
import KuikaEmptyState from "../../ui-kit/kuika-empty-state/kuika-empty-state";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox, { KuikaSelectboxOption } from "../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import KuikaTooltip from "../../ui-kit/kuika-tooltip/kuika-tooltip";

interface ISQLActionParametersState {
  search: string;
}
interface ISQLActionParametersProps {
  actionParameters?: IActionParameter[];
  updateParameters?: (actionParameters: IActionParameter[]) => void;
  preventShowingEmptyState?: boolean;
  /** Adds a search field that filters parameters by name. */
  searchable?: boolean;
  /** Shows a per-type icon in the type list and enables type search. */
  showTypeIcons?: boolean;
  /** Tipi hala "Unknown" olan satirlari hata rengiyle isaretler (kayit bu tipleri engelliyor). */
  markUnknownTypes?: boolean;
  /**
   * Names the server has already been asked about. A parameter is born "Unknown" — the statement is
   * parsed on every keystroke, the types are fetched after a pause — so without this the mark would
   * also cover the wait, and a freshly written query would show every parameter in the error colour
   * for as long as the debounce plus the round trip takes. Only a name in here is really unresolved.
   */
  checkedTypeNames?: string[];
  /** When set, the empty state is drawn with KuikaEmptyState. */
  /** Shows a search row above the type list. */
  searchableTypes?: boolean;
  emptyStateName?: string;
  emptyStateIcon?: string;
  emptyStateDescription?: string;
}

/** Parametre tipi secenegi; testId/onboardingId mevcut E2E ve onboarding kancalarini korur. */
interface IParameterTypeOption {
  value: BackEndValueType;
  testId: string;
  onboardingId: string;
}

const PARAMETER_TYPE_OPTIONS: IParameterTypeOption[] = [
  { value: "Unknown", testId: "Unknown", onboardingId: "unknown" },
  { value: "Boolean", testId: "Boolean", onboardingId: "boolean" },
  { value: "Bytea", testId: "Bytea", onboardingId: "bytea" },
  { value: "Byte", testId: "Byte", onboardingId: "byte" },
  { value: "DateTimeOffset", testId: "DTO", onboardingId: "dto" },
  { value: "Decimal", testId: "Decimal", onboardingId: "decimal" },
  { value: "Double", testId: "Double", onboardingId: "double" },
  { value: "Guid", testId: "Guid", onboardingId: "guid" },
  { value: "Int16", testId: "Int16", onboardingId: "int16" },
  { value: "Int32", testId: "Int32", onboardingId: "int32" },
  { value: "Int64", testId: "Int64", onboardingId: "int64" },
  { value: "Single", testId: "Single", onboardingId: "single" },
  { value: "String", testId: "Strings", onboardingId: "string" }
];

interface ITruncatedLabelProps {
  label: string;
  className: string;
  wrapperClassName: string;
  /** Icon pinned to the left of the text. */
  icon?: React.ReactNode;
  /** An option's label carries the row's E2E and onboarding hooks. */
  testId?: string;
  onboardingId?: string;
}

/** Shows a tooltip with the full value only when the text is actually clipped. */
const TruncatedLabel: React.FC<ITruncatedLabelProps> = ({
  label,
  className,
  wrapperClassName,
  icon,
  testId,
  onboardingId
}) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState<boolean>(false);

  useEffect(() => {
    const element: HTMLSpanElement | null = textRef.current;
    if (!element) return undefined;
    // The select can be wider on first paint; re-measure whenever the box changes.
    const measure = (): void => setIsTruncated(element.scrollWidth > element.clientWidth + 1);
    measure();
    const observer: ResizeObserver = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [label, isTruncated]);

  const body: React.ReactElement = (
    <span className={wrapperClassName} data-testid={testId} data-onboarding-id={onboardingId}>
      {icon}
      <span ref={textRef} className={className}>
        {label}
      </span>
    </span>
  );

  if (!isTruncated) return body;

  return (
    <KuikaTooltip content={label} position="top">
      {body}
    </KuikaTooltip>
  );
};

class SQLActionParameters extends PureSdComponent<ISQLActionParametersProps & IWithSDProps, ISQLActionParametersState> {
  constructor(props) {
    super(props);
    this.state = { search: "" };
  }

  /** The type list for one parameter; each label carries that row's E2E and onboarding hooks. */
  getTypeOptions = (parameterName: string): KuikaSelectboxOption[] =>
    PARAMETER_TYPE_OPTIONS.map((option: IParameterTypeOption) => ({
      value: option.value,
      // The label can be an icon and a span, so the search needs the plain name.
      searchLabel: option.value,
      label: this.renderTypeLabel(parameterName, option)
    }));

  renderTypeLabel = (parameterName: string, option: IParameterTypeOption): React.ReactNode => {
    const iconName: string | undefined = getDataTypeIconName(option.value);
    const testId: string = `${parameterName}${option.testId}Option`;
    const onboardingId: string = `common_sql-params_${parameterName}-${option.onboardingId}`;

    if (!this.props.showTypeIcons || !iconName) {
      return (
        <span data-testid={testId} data-onboarding-id={onboardingId}>
          {option.value}
        </span>
      );
    }

    return (
      <TruncatedLabel
        label={option.value}
        wrapperClassName="kuika__action-parameters__type-label"
        className="kuika__action-parameters__type-label-text"
        icon={<KuikaSvg type="action-icons" name={iconName} width={16} height={16} />}
        testId={testId}
        onboardingId={onboardingId}
      />
    );
  };

  getCommentedParams = (code?: string) => {
    const comments = code?.match(/--(.*)/g);
    const commentsArray = comments ?? [];
    let paramsFromComments: any = [];
    if (commentsArray) {
      paramsFromComments = commentsArray?.map((x) => x?.match(/@(\w+)/g));
      if (paramsFromComments) {
        paramsFromComments = paramsFromComments?.map((x) => x?.map((y) => y?.replace(/^@/, "")));
        paramsFromComments = paramsFromComments?.map((x) => x?.[0]);
      }
    }
    return paramsFromComments;
  };

  handleChangeParameterOption = (typeName: BackEndValueType, index: number): void => {
    if (!this.props.actionParameters || !this.props.updateParameters) return;

    const parameters: IActionParameter[] = [...this.props.actionParameters];
    parameters[index].Typename = typeName;
    this.props.updateParameters(parameters);
  };

  render() {
    if (
      (!this.props.actionParameters || this.props.actionParameters.length === 0) &&
      !this.props.preventShowingEmptyState
    ) {
      if (this.props.emptyStateIcon && this.props.emptyStateName) {
        return (
          <div className="kuika__action-parameters__empty-state">
            <KuikaEmptyState
              svgName={this.props.emptyStateIcon}
              name={this.props.emptyStateName}
              description={this.props.emptyStateDescription}
            />
          </div>
        );
      }

      return (
        <div className="kuika_centered" style={{ paddingTop: 90 }}>
          <NoPropertiesActionSvg />
        </div>
      );
    }

    const search: string = this.state.search.toLowerCase();

    return (
      <div className="kuika__action-parameters__container">
        {this.props.searchable && (
          <div className="kuika__action-parameters__search">
            <KuikaInput
              placeholder="Search parameters..."
              value={this.state.search}
              onChange={(_event, value: string | number): void => this.setState({ search: String(value ?? "") })}
              leftIcon={<KuikaSvg name="search" width={14} height={14} />}
              rightIcon={
                this.state.search ? (
                  <KuikaSvg
                    name="x"
                    width={12}
                    height={12}
                    pointer
                    onClick={(): void => this.setState({ search: "" })}
                  />
                ) : undefined
              }
              data-testid="actionParametersSearch"
            />
          </div>
        )}
        {this.props.actionParameters
          ?.filter((item) => !this.getCommentedParams().includes(item.name))
          ?.filter((item) => (search ? (item.displayName ?? item.name).toLowerCase().includes(search) : true))
          ?.map((item, index) => {
            return (
              <div
                key={index}
                className={`kuika__action-parameters__item${
                  this.props.markUnknownTypes &&
                  item.Typename === "Unknown" &&
                  this.props.checkedTypeNames?.includes(item.name)
                    ? " kuika__action-parameters__item--invalid"
                    : ""
                }`}
              >
                <TruncatedLabel
                  label={item.displayName ?? item.name}
                  wrapperClassName="kuika__action-parameters__item-name"
                  className="kuika__action-parameters__item-name-text"
                />
                <div className="kuika__action-parameters__item-control">
                  <KuikaSelectbox
                    data-testid={`${item.name}Dropdown`}
                    data-onboarding-id={`common_sql-params_${item.name}-dropdown`}
                    disabled={item.name === "GeminiFileParameter"}
                    className="kuika__action-parameters__item-select"
                    value={item.Typename}
                    options={this.getTypeOptions(item.name)}
                    searchable={this.props.searchableTypes}
                    // The names are longer than a control this narrow; the list keeps its own width.
                    dropdownMatchSelectWidth={false}
                    onChange={(typeName): void => {
                      // The picked value comes back untyped; the list is what narrows it.
                      const picked: IParameterTypeOption | undefined = PARAMETER_TYPE_OPTIONS.find(
                        (option: IParameterTypeOption) => option.value === typeName
                      );
                      if (picked) this.handleChangeParameterOption(picked.value, index);
                    }}
                  />
                </div>
              </div>
            );
          })}
      </div>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    return false;
  };
}

const sqlActionParameters = withSD<ISQLActionParametersProps>(SQLActionParameters);
export { sqlActionParameters as SQLActionParameters };
